import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

// Create auth context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // Check token validity
  const checkTokenValidity = () => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    try {
      // Decode the JWT token
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      
      // Check if token is expired
      const isExpired = tokenData.exp * 1000 <= Date.now();
      if (isExpired) {
        console.log('Token has expired');
        return false;
      }
      
      // Check if user_id exists in the token
      if (!tokenData.user_id && !tokenData.id) {
        console.log('User ID missing from token');
        return false;
      }
      
      return true;
    } catch (e) {
      console.error('Error validating token:', e);
      return false;
    }
  };

  // Logout user
  const logout = () => {
    // Clear user data from state
    setCurrentUser(null);
    
    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Reset error state
    setError(null);
    
    // Redirect to login page using hash router format
    navigate('/login');
  };

  useEffect(() => {
    // Check if user is logged in on component mount
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        // Validate token first
        if (!checkTokenValidity()) {
          console.log('Invalid or expired token detected during initialization');
          logout();
          return;
        }
        
        const user = JSON.parse(userData);
        setCurrentUser(user);
        
        // Token will be automatically included by the API service interceptors
      } catch (error) {
        console.error('Failed to parse user data', error);
        logout();
      }
    }
    
    setLoading(false);
  }, [navigate]); // Only depend on navigate, not logout
  
  // Periodic token validation
  useEffect(() => {
    // Only run validation if user is logged in
    if (!currentUser) return;
    
    // Check token validity every 5 minutes
    const tokenValidationInterval = setInterval(() => {
      console.log('Performing periodic token validation check');
      if (!checkTokenValidity()) {
        console.log('Token validation failed during periodic check');
        logout();
      }
    }, 5 * 60 * 1000); // 5 minutes in milliseconds
    
    // Also validate token on window focus
    const handleWindowFocus = () => {
      console.log('Window focused, checking token validity');
      if (!checkTokenValidity()) {
        console.log('Token validation failed on window focus');
        logout();
      }
    };
    
    window.addEventListener('focus', handleWindowFocus);
    
    // Cleanup
    return () => {
      clearInterval(tokenValidationInterval);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [currentUser]); // Depend on currentUser to reset interval when user changes

  // Register new user
  const register = async (name, email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authAPI.register({
        name,
        email,
        password
      });
      
      setLoading(false);
      
      // Return the response data for handling in the component
      return response.data;
    } catch (error) {
      setLoading(false);
      
      if (error.response) {
        setError(error.response.data.message || 'Registration failed');
        return error.response.data;
      } else {
        setError('Network error. Please try again.');
        return { status: 500, message: 'Network error' };
      }
    }
  };

  // Login user
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authAPI.login({
        email,
        password
      });
      
      const { token, user } = response.data.data;
      
      // Save token and user data to local storage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Set current user
      setCurrentUser(user);
      
      setLoading(false);
      
      return response.data;
    } catch (error) {
      setLoading(false);
      console.error('Login error:', error);
      
      if (error.response) {
        const errorMessage = error.response.data.message || 'Login failed';
        setError(errorMessage);
        return {
          status: error.response.status,
          message: errorMessage,
          data: error.response.data
        };
      } else {
        setError('Network error. Please try again.');
        return { status: 500, message: 'Network error' };
      }
    }
  };

  // Logout user is already defined above

  // Verify email
  const verifyEmail = async (token, email) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authAPI.verifyEmail(token, email);
      
      setLoading(false);
      return response.data;
    } catch (error) {
      setLoading(false);
      
      if (error.response) {
        setError(error.response.data.message || 'Verification failed');
        return error.response.data;
      } else {
        setError('Network error. Please try again.');
        return { status: 500, message: 'Network error' };
      }
    }
  };

  // Resend verification email
  const resendVerification = async (email) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authAPI.resendVerification(email);
      
      setLoading(false);
      return response.data;
    } catch (error) {
      setLoading(false);
      
      if (error.response) {
        setError(error.response.data.message || 'Failed to resend verification email');
        return error.response.data;
      } else {
        setError('Network error. Please try again.');
        return { status: 500, message: 'Network error' };
      }
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    // First check if we have a token, user data, and current user in state
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData || !currentUser) {
      return false;
    }
    
    // Then validate the token
    return checkTokenValidity();
  };

  // Context value
  const value = {
    currentUser,
    loading,
    error,
    register,
    login,
    logout,
    verifyEmail,
    resendVerification,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
